import { AccumulatedTeamCategoriesByGameStats, SeasonID, SeasonTeamAbrvID } from '@shared/types';
import { collection, getDocs } from 'firebase/firestore';
import { db } from 'lib/firebase';

/**
 * Fetches weekly breakdown of stats for all teams for a specific season.
 * Path: stats/${yearID}/teamresults/${yearID}-00-Season/${yearID}-Teams
 *
 * @param {SeasonID} yearID - The season year (e.g., '2025')
 *
 * @return {Promise<AccumulatedTeamCategoriesByGameStats>} Weekly stats keyed by seasonTeamKey
 */
export const fetchTeamWeeklyStatsBySeasonID = async (
  yearID: SeasonID
): Promise<AccumulatedTeamCategoriesByGameStats> => {
  const collectionPath = `stats/${yearID}/teamresults/${yearID}-00-Season/${yearID}-Teams`;
  const collectionRef = collection(db, collectionPath);
  const snapshot = await getDocs(collectionRef);

  const weeklyStats: AccumulatedTeamCategoriesByGameStats = {};

  snapshot.forEach((doc) => {
    const seasonTeamAbrvID = doc.id as SeasonTeamAbrvID;
    const teamStats = doc.data();
    weeklyStats[seasonTeamAbrvID] = teamStats as AccumulatedTeamCategoriesByGameStats[SeasonTeamAbrvID];
  });

  return weeklyStats;
};

/**
 * Fetches cumulative season stats for all teams for a specific season.
 * Path: stats/${yearID}/teamresults/${yearID}-00-Season/${yearID}-Cumulative
 *
 * @param {SeasonID} yearID - The season year (e.g., '2025')
 *
 * @return {Promise<AccumulatedTeamCategoriesByGameStats>} Cumulative stats keyed by seasonTeamKey
 */
export const fetchTeamCumulativeStatsBySeasonID = async (
  yearID: SeasonID
): Promise<AccumulatedTeamCategoriesByGameStats> => {
  const collectionPath = `stats/${yearID}/teamresults/${yearID}-00-Season/${yearID}-Cumulative`;
  const collectionRef = collection(db, collectionPath);
  const snapshot = await getDocs(collectionRef);

  const cumulativeStats: AccumulatedTeamCategoriesByGameStats = {};

  snapshot.forEach((doc) => {
    const seasonTeamAbrvID = doc.id as SeasonTeamAbrvID;
    const teamStats = doc.data();
    cumulativeStats[seasonTeamAbrvID] = teamStats as AccumulatedTeamCategoriesByGameStats[SeasonTeamAbrvID];
  });

  return cumulativeStats;
};

/**
 * Fetches rank values (1-32) for each category for all teams for a specific season.
 * Path: stats/${yearID}/teamresults/${yearID}-00-Season/${yearID}-Ranks
 *
 * @param {SeasonID} yearID - The season year (e.g., '2025')
 *
 * @return {Promise<AccumulatedTeamCategoriesByGameStats>} Team ranks keyed by seasonTeamKey
 */
export const fetchTeamRanksBySeasonID = async (yearID: SeasonID): Promise<AccumulatedTeamCategoriesByGameStats> => {
  const collectionPath = `stats/${yearID}/teamresults/${yearID}-00-Season/${yearID}-Ranks`;
  const collectionRef = collection(db, collectionPath);
  const snapshot = await getDocs(collectionRef);

  const teamRanks: AccumulatedTeamCategoriesByGameStats = {};

  snapshot.forEach((doc) => {
    const seasonTeamAbrvID = doc.id as SeasonTeamAbrvID;
    const teamRank = doc.data();
    teamRanks[seasonTeamAbrvID] = teamRank as AccumulatedTeamCategoriesByGameStats[SeasonTeamAbrvID];
  });

  return teamRanks;
};
